/**
 * "Give a free month" — the view logic of the card on a user's detail in the Admin Portal
 * (free month for paying subscribers, PR-2, 2026-09-30; routers/beta_user_admin.py /free-month,
 * services/free_month_gift.py; docs/architecture/38-PARTNER-ATTRIBUTION.md §19).
 *
 * What a reader must be able to trust:
 *  - the button shows only when the server says the feature is on (GET /free-month/config — the
 *    FF_ADMIN_FREE_MONTH kill switch) AND the session can write (admin / super admin);
 *  - nothing is given without a preflight the admin has read (where the month goes, the new end or
 *    next charge, the warnings) and a confirm in the page;
 *  - one idempotency key per person until a terminal answer: a double click, a retry, or closing and
 *    reopening after a lost response re-sends the SAME key (the server answers from its ledger — never
 *    a second month); a NEW key is minted only after an answer arrived (review of #4306, LOW 1);
 *  - the preflight lists this person's recent / queued / in-flight free months, so a second month (or
 *    a second moved charge) is never given unwarned (LOW 2);
 *  - the words shown to an admin say "Coach Cedar", never bare "Cedar".
 *
 * Pure (no React, no fetch) so jest can pin it: web/src/components/admin/__tests__/
 * giveFreeMonthLogic.test.ts. Web-only (the admin SPA).
 */

export const ADMIN_PREFIX = '/api/admin';
const seg = (userId: string) => encodeURIComponent(userId);

export const configPath = (userId: string) => `${ADMIN_PREFIX}/beta-users/free-month/config?user_id=${seg(userId)}`;
export const preflightPath = (userId: string) => `${ADMIN_PREFIX}/beta-users/free-month/preflight?user_id=${seg(userId)}`;
export const givePath = `${ADMIN_PREFIX}/beta-users/free-month`;

export type GiftReason = 'goodwill' | 'service_issue' | 'promotion';
export const GIFT_REASONS: { value: GiftReason; label: string }[] = [
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'service_issue', label: 'Service issue (an outage or a bug on our side)' },
  { value: 'promotion', label: 'Promotion' },
];

export const NOTES_MAX = 500;

export interface FreeMonthConfig {
  enabled: boolean;
  days: number;
  reasons: string[];
}

export interface FreeMonthPreflight {
  outcome: 'deliver' | 'queue' | 'refuse';
  route: string | null;
  reason: string | null;
  payer_state: string;
  store: string | null;
  current_end: string | null;
  new_end: string | null;
  next_attempt_at: string | null;
  apple_extensions_last_365_days: number;
  days: number;
  warnings: string[];
  recent_months?: string[];
}

export interface FreeMonthResult {
  outcome: 'delivered' | 'queued' | 'pending' | 'refused' | 'conflict';
  route: string | null;
  reason: string | null;
  ledger_id: string | null;
  ledger_status: string | null;
  payer_state: string | null;
  store: string | null;
  ends_at: string | null;
  next_attempt_at: string | null;
  replay: boolean;
  message: string;
}

/** Show the button only when the server says it is on and this session may write. */
export function showGiveButton(config: FreeMonthConfig | null, writable: boolean): boolean {
  return !!config && config.enabled === true && writable === true;
}

/** A fresh key for one dialog: `fm-` + a random id (8–120 of [A-Za-z0-9._:-], as the server checks). */
export function newIdempotencyKey(rand: () => string = defaultRandom): string {
  const raw = rand().replace(/[^A-Za-z0-9._:-]/g, '');
  const key = `fm-${raw}`;
  return key.length >= 8 ? key.slice(0, 120) : `${key}${'0'.repeat(8 - key.length)}`;
}

function defaultRandom(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

export function giveBody(userId: string, reason: GiftReason, notes: string, idempotencyKey: string) {
  return { user_id: userId, reason, notes: (notes || '').slice(0, NOTES_MAX), idempotency_key: idempotencyKey };
}

/** The key the next "Give" uses: the current one until an answer arrived for it (a network error or a
 *  lost response keeps it, so a retry is answered from the ledger), a fresh one after. */
export function nextKey(current: string | null, spent: boolean, mint: () => string = newIdempotencyKey): string {
  return current && !spent ? current : mint();
}

/** After a failed request: a 4xx is the server's answer (the key is done — a 409 would repeat forever);
 *  a 5xx or no answer at all keeps the key, so the retry is answered from the ledger. */
export function spentAfterError(status: number | null): boolean {
  return status !== null && status >= 400 && status < 500;
}

/** A terminal answer ends the dialog's key: the next "Give" is a NEW month. A pending or queued
 *  month is terminal for the dialog too — the ledger finishes it; giving again is a second month. */
export function keyIsSpent(result: FreeMonthResult | null): boolean {
  return !!result;
}

const ROUTE_WORDS: Record<string, string> = {
  promotional: 'Free Coach Cedar access (a promotional month)',
  promotional_stack: 'Added to the end of their running free period',
  apple_extend: 'The App Store moves their next charge back a month',
  google_defer: 'Google Play moves their next charge back a month',
};

export function formatDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** What the preflight says, as lines for the confirm step. */
export function preflightLines(p: FreeMonthPreflight): string[] {
  const lines: string[] = [];
  if (p.outcome === 'deliver') {
    lines.push(ROUTE_WORDS[p.route || ''] || `Route: ${p.route}`);
    const isStore = p.route === 'apple_extend' || p.route === 'google_defer';
    if (p.current_end) lines.push(`${isStore ? 'Next charge now' : 'Free access now ends'}: ${formatDay(p.current_end)}`);
    if (p.new_end) lines.push(`${isStore ? 'Next charge after' : 'Free access will end'}: ${formatDay(p.new_end)}`);
  } else if (p.outcome === 'queue') {
    lines.push('It will be QUEUED, not given now.');
  } else {
    lines.push('It would be REFUSED — nothing would be given.');
  }
  const recent = (p.recent_months || []).map((m) => `Earlier: ${m}`);
  return [...lines, ...(p.warnings || []), ...recent];
}

export function canConfirm(p: FreeMonthPreflight | null, busy: boolean): boolean {
  return !!p && p.outcome !== 'refuse' && !busy;
}

export type Tone = 'ok' | 'warn' | 'error';

/** The result, as the card shows it. */
export function describeResult(r: FreeMonthResult): { tone: Tone; headline: string; detail: string } {
  const again = r.replay ? ' (this dialog had already given it — nothing was sent twice)' : '';
  switch (r.outcome) {
    case 'delivered': {
      const isStore = r.route === 'apple_extend' || r.route === 'google_defer';
      const when = r.ends_at ? ` ${isStore ? 'Next charge' : 'Free access until'}: ${formatDay(r.ends_at)}.` : '';
      return { tone: 'ok', headline: `Free month given${again}.`, detail: `${ROUTE_WORDS[r.route || ''] || ''}.${when} They get a push and an email from Coach Cedar.` };
    }
    case 'queued':
      return { tone: 'warn', headline: `Queued${again}.`, detail: `${r.message}${r.next_attempt_at ? ` Next try: ${formatDay(r.next_attempt_at)}.` : ''}` };
    case 'pending':
      return { tone: 'warn', headline: `Sent — confirming${again}.`, detail: r.message };
    case 'conflict':
      return { tone: 'error', headline: 'Not given.', detail: r.message };
    default:
      return { tone: 'error', headline: 'Not given.', detail: r.message || `Refused (${r.reason || 'unknown'}).` };
  }
}

/** A non-2xx answer in the admin's words. */
export function describeError(status: number, bodyText: string): string {
  let detail: unknown = null;
  try {
    const parsed = JSON.parse(bodyText || '');
    detail = parsed && typeof parsed === 'object' && 'detail' in parsed ? (parsed as { detail: unknown }).detail : null;
  } catch {
    detail = null;
  }
  if (detail && typeof detail === 'object' && typeof (detail as { message?: string }).message === 'string') {
    return (detail as { message: string }).message;
  }
  if (typeof detail === 'string' && detail) return detail;
  if (status === 503) return 'Give a free month is switched off.';
  if (status === 403) return 'Your admin role cannot give a free month (admin or super admin only).';
  if (status === 401) return 'Your admin session has expired. Sign in again.';
  if (status === 409) return 'That dialog was already used for a different free month. Close it and start again.';
  return `Something went wrong (${status}). Nothing is sent twice — check the user before trying again.`;
}
